import type { Checklist, ChecklistConditionGroup, ChecklistConditionRule, ClientGroup, CustomFieldValues } from "~/types";

export const hasActiveConditions = (checklist: Checklist) => {
  return checklist.conditions?.groups.length ||
    checklist.conditions?.rules.length
}

export const mapChecklistConditionGroupForBackend = (group: ChecklistConditionGroup): any => {
  if('uuid' in group) {
    delete group.uuid;
  }

  return {
    ...group,
    rules: group.rules.map(rule => mapChecklistConditionRuleForBackend(rule)),
    groups: group.groups.map(group => mapChecklistConditionGroupForBackend(group))
  }
}

export const mapChecklistConditionRuleForBackend = (rule: ChecklistConditionRule): any => {
  if('uuid' in rule) {
    delete rule.uuid;
  }

  if('clientGroup' in rule) {
    // Item of type "client-group"
    return {
      ...rule,
      clientGroup: rule.clientGroup && typeof rule.clientGroup === 'object' ? rule.clientGroup._id : rule.clientGroup
    }
  }

  if('customField' in rule) {
    // Item of type "custom-field"
    return {
      ...rule,
      customField: rule.customField && typeof rule.customField === 'object' ? rule.customField._id : rule.customField
    }
  }
}

export const areGroupConditionsMet = (rootGroup: ChecklistConditionGroup | undefined, clientGroupIds: ClientGroup['_id'][], customFieldValues: CustomFieldValues): boolean => {
  const { isProfessionalRollout } = useRolloutPlans();
  if(!isProfessionalRollout.value) {
    return true;
  }

  if(!rootGroup || (!rootGroup.groups.length && !rootGroup.rules.length)) {
    return true;
  }

  if(rootGroup.condition.and) {
    const areRulesMet = rootGroup.rules.length ? rootGroup.rules.every(rule => areRuleConditionsMet(rule, clientGroupIds, customFieldValues)) : true;
    const areGroupsMet = rootGroup.groups.length ? rootGroup.groups.every(group => areGroupConditionsMet(group, clientGroupIds, customFieldValues)) : true;

    return rootGroup.condition.not ? !(areRulesMet && areGroupsMet) : areRulesMet && areGroupsMet;
  }
  else {
    // or is selected
    const areRulesMet = rootGroup.rules.length ? rootGroup.rules.some(rule => areRuleConditionsMet(rule, clientGroupIds, customFieldValues)) : false;
    const areGroupsMet = rootGroup.groups.length ? rootGroup.groups.some(group => areGroupConditionsMet(group, clientGroupIds, customFieldValues)) : false;

    return rootGroup.condition.not ? !(areRulesMet || areGroupsMet) : areRulesMet || areGroupsMet;
  }
}

const areRuleConditionsMet = (rule: ChecklistConditionRule, clientGroupIds: ClientGroup['_id'][], customFieldValues: CustomFieldValues) => {
  if(rule.type === 'client-group') {
    const clientGroupId = typeof rule.clientGroup === 'object' ? rule.clientGroup!._id : rule.clientGroup;
    return clientGroupIds.some(id => clientGroupId === id);
  }

  // Rule is of type 'custom-field'
  const customFieldId = typeof rule.customField === 'object' ? rule.customField!._id : rule.customField;
  if(Array.isArray(rule.compareValue)) {
    // array-based custom field
    return rule.compareValue.every(val => customFieldValues[customFieldId]?.includes(val));
  }
  else {
    // text-based custom field
    return customFieldValues[customFieldId] === rule.compareValue;
  }
}